import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, ComboBox, useDialog } from '@gokrew/ui';
import {
  Pencil,
  Mail,
  Building2,
  UserRound,
  CalendarDays,
  Plus,
  Phone,
  History,
  Presentation,
} from 'lucide-react';
import {
  addCrmActivity,
  getCrmDeal,
  getCrmDealActivities,
  moveCrmDeal,
  saveCrmDeal,
} from '@/api/crm';
import type { CrmActivityInput, CrmDeal, CrmDealStage } from '@/api/crm';
import { CrmDealEditor } from './CrmDealEditor';
import { useLiveDemoCreate } from './crm-live-demo-dialog';
import { CrmActivityFeed } from './CrmActivityFeed';
import { CrmButton, CrmEmpty, CrmError, CrmRequired } from './CrmShared';
import {
  activityDates,
  CRM_LOG_KINDS,
  useCrmFormat,
  DEAL_STAGES,
  nowLocal,
} from './crm-format';
import styles from './Crm.module.css';
type DealDetails = { notes: string; expectedCloseDate: string };
type DetailsDraft = DealDetails & { base: DealDetails };
const detailsOf = (deal: CrmDeal): DealDetails => ({
  notes: deal.notes ?? '',
  expectedCloseDate: deal.expectedCloseDate ?? '',
});
const untouched = (draft: DetailsDraft) =>
  draft.notes === draft.base.notes &&
  draft.expectedCloseDate === draft.base.expectedCloseDate;
export const CrmDealDetail = ({
  id,
  dialogId,
}: {
  id: string;
  dialogId?: string;
}) => {
  const { t, money } = useCrmFormat();
  const client = useQueryClient();
  const { openDialog, closeDialog } = useDialog();
  const createDemo = useLiveDemoCreate();
  const deal = useQuery({
    queryKey: ['crm', 'deal', id],
    queryFn: () => getCrmDeal(id),
  });
  const activities = useQuery({
    queryKey: ['crm', 'deal', id, 'activities'],
    queryFn: () => getCrmDealActivities(id),
  });
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [entry, setEntry] = useState<CrmActivityInput>({
    kind: 'note',
    title: '',
    body: '',
    dueAt: null,
  });
  const [due, setDue] = useState(nowLocal);
  const [draft, setDraft] = useState<DetailsDraft | null>(null);
  const [saved, setSaved] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  // Follow the deal (e.g. edited in the Edit dialog) until the user types.
  useEffect(() => {
    if (deal.data) {
      const next = detailsOf(deal.data);
      setDraft((old) =>
        !old || untouched(old) ? { ...next, base: next } : old
      );
    }
  }, [deal.data]);
  const dirty =
    !!deal.data &&
    !!draft &&
    (draft.notes !== (deal.data.notes ?? '') ||
      draft.expectedCloseDate !== (deal.data.expectedCloseDate ?? ''));
  /** Saves notes and close date over the latest copy of the deal, so a stage
   * move made meanwhile is kept; on a conflict the draft stays for a retry. */
  const saveDetails = async (event: FormEvent, current: CrmDeal) => {
    event.preventDefault();
    if (busy || !draft) return;
    setBusy(true);
    setDetailsError('');
    setSaved(false);
    try {
      await saveCrmDeal(
        {
          name: current.name,
          companyId: current.companyId,
          contactId: current.contactId || null,
          ownerUserId: current.ownerUserId,
          stage: current.stage,
          product: current.product,
          estimatedEmployees: current.estimatedEmployees,
          atsMrrCents: current.atsMrrCents,
          expectedCloseDate: draft.expectedCloseDate || null,
          notes: draft.notes,
          version: current.version,
        },
        current.id
      );
      setDraft(
        (old) =>
          old && {
            ...old,
            base: {
              notes: old.notes,
              expectedCloseDate: old.expectedCloseDate,
            },
          }
      );
      await client.invalidateQueries({ queryKey: ['crm'] });
      setSaved(true);
    } catch (err) {
      setDetailsError(
        t(
          (err as { response?: { status?: number } }).response?.status === 409
            ? 'crm.workspace.deal_conflict'
            : 'crm.save_error'
        )
      );
      await deal.refetch();
    } finally {
      setBusy(false);
    }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await addCrmActivity(id, {
        ...entry,
        title: entry.title.trim(),
        ...activityDates(entry.kind, due),
      });
      setEntry((old) => ({ ...old, title: '', body: '', dueAt: null }));
      setDue(entry.kind === 'task' ? '' : nowLocal());
      await client.invalidateQueries({ queryKey: ['crm'] });
    } catch {
      setError(t('crm.save_error'));
    } finally {
      setBusy(false);
    }
  };
  const move = async (stage: CrmDealStage) => {
    if (!deal.data || busy) return;
    setBusy(true);
    setError('');
    try {
      await moveCrmDeal(deal.data, stage);
      await client.invalidateQueries({ queryKey: ['crm'] });
    } catch (err) {
      setError(
        t(
          (err as { response?: { status?: number } }).response?.status === 409
            ? 'crm.conflict'
            : 'crm.save_error'
        )
      );
      await deal.refetch();
    } finally {
      setBusy(false);
    }
  };
  if (deal.isPending) return <p role="status">{t('crm.loading')}</p>;
  if (deal.isError) return <CrmError retry={() => void deal.refetch()} />;
  const value = deal.data;
  const owner =
    [value.owner?.firstName, value.owner?.lastName].filter(Boolean).join(' ') ||
    value.owner?.email ||
    t('crm.workspace.unassigned');
  const all = activities.data ?? [];
  const inTab = (key: string, activity: (typeof all)[number]) =>
    key === 'log'
      ? CRM_LOG_KINDS.includes(activity.kind)
      : !CRM_LOG_KINDS.includes(activity.kind) &&
        (key === 'all' ||
          (key === 'emails'
            ? activity.kind.includes('email')
            : key === 'calls'
              ? // Calendar meetings are the calls (Meet, Teams, phone).
                ['call', 'meeting'].includes(activity.kind)
              : ['note', 'call', 'meeting', 'task'].includes(activity.kind)));
  const visible = all.filter((activity) => inTab(filter, activity));
  const count = (key: string) =>
    all.filter((activity) => inTab(key, activity)).length;
  return (
    <div className={styles.dealDetail}>
      <aside className={styles.dealFacts}>
        <div className={styles.dealDetailHeading}>
          <span className={styles.chip}>
            {t(`crm.products.${value.product}`)}
          </span>
          <CrmButton
            variant="ghost"
            IconPrefix={Pencil}
            onClick={() =>
              openDialog({
                title: t('crm.workspace.edit_deal'),
                containerClass: styles.editorDialog,
                content: (
                  <CrmDealEditor
                    deal={value}
                    onDeleted={() => dialogId && closeDialog(dialogId)}
                  />
                ),
              })
            }
          >
            {t('crm.edit')}
          </CrmButton>
          <CrmButton
            variant="ghost"
            IconPrefix={Presentation}
            onClick={() => createDemo({ id: value.id, name: value.name })}
          >
            {t('crm.live_demo.button')}
          </CrmButton>
        </div>
        <h2>{value.name}</h2>
        <label className={styles.stagePicker}>
          {t('crm.stage')}
          <ComboBox
            size="sm"
            disabled={busy}
            allowUnselect={false}
            value={value.stage}
            options={DEAL_STAGES.map((stage) => ({
              value: stage,
              label: t(`crm.workspace.stages.${stage}`),
            }))}
            onChange={(stage) => stage && void move(stage)}
          />
        </label>
        <div className={styles.detailRevenue}>
          <span>
            {t('crm.arr')}
            <strong>{money(value.arrCents)}</strong>
          </span>
        </div>
        <dl className={styles.factList}>
          <div>
            <dt>
              <Building2 />
              {t('crm.workspace.company')}
            </dt>
            <dd>{value.company?.name || t('crm.unknown')}</dd>
          </div>
          <div>
            <dt>
              <UserRound />
              {t('crm.workspace.primary_contact')}
            </dt>
            <dd>
              {value.contact?.name || t('crm.workspace.not_added')}
              {value.contact?.email && (
                <a href={`mailto:${value.contact.email}`}>
                  {value.contact.email}
                </a>
              )}
            </dd>
          </div>
          <div>
            <dt>
              <UserRound />
              {t('crm.workspace.owner')}
            </dt>
            <dd>{owner}</dd>
          </div>
          <div>
            <dt>{t('crm.estimatedEmployees')}</dt>
            <dd>{value.estimatedEmployees ?? t('crm.unknown')}</dd>
          </div>
          <div>
            <dt>{t('crm.source')}</dt>
            <dd>
              {value.company?.source
                ? t(`crm.sources.${value.company.source}`)
                : t('crm.unknown')}
              {value.company?.partner?.name && (
                <small>{value.company.partner.name}</small>
              )}
            </dd>
          </div>
        </dl>
        {draft && (
          <form
            className={`${styles.form} ${styles.dealNotes}`}
            onSubmit={(event) => void saveDetails(event, value)}
          >
            <label>
              <span className={styles.dealNotesLabel}>
                <CalendarDays />
                {t('crm.workspace.expected_close')}
              </span>
              <Input
                type="date"
                value={draft.expectedCloseDate}
                onChange={(event) => {
                  setSaved(false);
                  setDraft({ ...draft, expectedCloseDate: event.target.value });
                }}
              />
            </label>
            <label>
              {t('crm.notes')}
              <textarea
                rows={3}
                maxLength={10000}
                placeholder={t('crm.workspace.notes_placeholder')}
                value={draft.notes}
                onChange={(event) => {
                  setSaved(false);
                  setDraft({ ...draft, notes: event.target.value });
                }}
              />
            </label>
            {detailsError && (
              <p role="alert" className={styles.error}>
                {detailsError}
              </p>
            )}
            <div className={styles.dealNotesFooter}>
              {saved && !dirty && (
                <span role="status">{t('crm.workspace.deal_saved')}</span>
              )}
              <CrmButton
                type="submit"
                variant="adminPrimary"
                disabled={busy || !dirty}
              >
                {t(busy ? 'crm.saving' : 'crm.save')}
              </CrmButton>
            </div>
          </form>
        )}
      </aside>
      <section className={styles.dealTimeline}>
        <h2>{t('crm.workspace.deal_activity')}</h2>
        <form
          className={`${styles.form} ${styles.activityComposer}`}
          onSubmit={submit}
        >
          <div className={styles.composerKinds}>
            {(['note', 'call', 'meeting', 'task'] as const).map((kind) => (
              <button
                type="button"
                key={kind}
                className={entry.kind === kind ? styles.composerActive : ''}
                onClick={() => {
                  setEntry((old) => ({ ...old, kind }));
                  // A task is due on a day (empty until chosen).
                  if ((kind === 'task') !== (entry.kind === 'task'))
                    setDue(kind === 'task' ? '' : nowLocal());
                }}
              >
                {t(`crm.workspace.activityFilters.${kind}`)}
              </button>
            ))}
          </div>
          <Input
            required
            aria-label={t('crm.workspace.activity_title')}
            placeholder={t(`crm.workspace.compose.${entry.kind}`)}
            maxLength={300}
            value={entry.title}
            onChange={(event) =>
              setEntry((old) => ({ ...old, title: event.target.value }))
            }
          />
          <textarea
            aria-label={t('crm.workspace.activity_details')}
            rows={2}
            placeholder={t('crm.workspace.activity_details')}
            maxLength={10000}
            value={entry.body || ''}
            onChange={(event) =>
              setEntry((old) => ({ ...old, body: event.target.value }))
            }
          />
          <div className={styles.composerFooter}>
            <label>
              <span>
                {t(
                  entry.kind === 'task'
                    ? 'crm.workspace.due_at'
                    : entry.kind === 'note'
                      ? 'crm.workspace.activity_date_note'
                      : 'crm.workspace.activity_date_event'
                )}
                {entry.kind === 'task' && <CrmRequired />}
              </span>
              <Input
                type={entry.kind === 'task' ? 'date' : 'datetime-local'}
                value={due}
                onChange={(event) => setDue(event.target.value)}
                required={entry.kind === 'task'}
              />
            </label>
            <CrmButton
              type="submit"
              IconPrefix={Plus}
              variant="adminPrimary"
              disabled={
                busy || !entry.title.trim() || (entry.kind === 'task' && !due)
              }
            >
              {t('crm.workspace.add_activity')}
            </CrmButton>
          </div>
        </form>
        {error && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}
        <div className={styles.feedTabs}>
          {['all', 'manual', 'calls', 'emails', 'log'].map((key) => (
            <button
              key={key}
              type="button"
              className={filter === key ? styles.feedTabActive : ''}
              onClick={() => setFilter(key)}
            >
              {key === 'emails' && <Mail />}
              {key === 'calls' && <Phone />}
              {key === 'log' && <History />}
              {t(`crm.workspace.timelineFilters.${key}`)}
              {!activities.isPending && (
                <span className={styles.tabCount}>{count(key)}</span>
              )}
            </button>
          ))}
        </div>
        {activities.isPending ? (
          <p role="status">{t('crm.loading')}</p>
        ) : activities.isError ? (
          <CrmError retry={() => void activities.refetch()} />
        ) : visible.length ? (
          <CrmActivityFeed activities={visible} />
        ) : (
          <CrmEmpty
            title={t('crm.workspace.no_activities')}
            description={t(
              filter === 'emails'
                ? 'crm.workspace.emails_empty'
                : filter === 'calls'
                  ? 'crm.workspace.calls_empty'
                  : filter === 'log'
                    ? 'crm.workspace.log_empty'
                    : 'crm.workspace.activities_empty'
            )}
          />
        )}
      </section>
    </div>
  );
};
